import React, { useCallback, useEffect, useRef, useState, } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import { debounce } from 'lodash-es';
import Portal from '../../Atoms/Portal';
import { DefaultTheme } from '../../Themes';
import { TooltipPlacement } from './types';
const StyledTooltipContainer = styled.div `
  position: relative;
  display: inline-flex;
`;
const StyledTooltip = styled.div `
  position: absolute;
  display: inline-flex;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  text-align: center;
  box-sizing: border-box;
  word-break: break-word;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: opacity 150ms linear;
  z-index: 2020;
  pointer-events: auto;
  cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};

  ${({ open }) => open && css `
    opacity: 1;
    visibility: visible;
  `}

  ${({ theme, color }) => css `
    background-color: ${color || theme.ThemeColors.DARK.hex};
    color: ${theme.ThemeColors.WHITE.hex};
  `}

  ${({ showArrow, theme, color }) => showArrow && css `
    &::after {
      content: '';
      display: inline-block;
      border: 0.416666667rem solid ${color || theme.ThemeColors.DARK.hex};
      position: absolute;
    }
  `}
`;
const verticallyCenteredTooltip = css `
  top: 50%;
  transform: translate3d(0, -50%, 0);
`;
const horizontallyCenteredTooltip = css `
  left: 50%;
  transform: translate3d(-50%, 0, 0);
`;
// Top-positioned tooltips
const StyledTopTooltip = styled(StyledTooltip) `
  bottom: 110%;

  &::after {
    border-bottom: 0;
    border-right-color: transparent;
    border-left-color: transparent;
    top: 100%;
    ${horizontallyCenteredTooltip}
  }
`;
const StyledTopStartTooltip = styled(StyledTopTooltip) `
  left: 0;
`;
const StyledTopCenterTooltip = styled(StyledTopTooltip) `
  ${horizontallyCenteredTooltip}
`;
const StyledTopEndTooltip = styled(StyledTopTooltip) `
  right: 0;
`;
// Right-positioned tooltips
const StyledRightTooltip = styled(StyledTooltip) `
  left: 110%;

  &::after {
    border-left: 0;
    border-top-color: transparent;
    border-bottom-color: transparent;
    right: 100%;
    margin-right: -1px;
    ${verticallyCenteredTooltip}
  }
`;
const StyledRightStartTooltip = styled(StyledRightTooltip) `
  top: 0;
`;
const StyledRightCenterTooltip = styled(StyledRightTooltip) `
  ${verticallyCenteredTooltip}
`;
const StyledRightEndTooltip = styled(StyledRightTooltip) `
  bottom: 0;
`;
// Bottom-positioned tooltips
const StyledBottomTooltip = styled(StyledTooltip) `
  top: 110%;

  &::after {
    border-top: 0;
    border-right-color: transparent;
    border-left-color: transparent;
    bottom: 100%;
    ${horizontallyCenteredTooltip}
  }
`;
const StyledBottomStartTooltip = styled(StyledBottomTooltip) `
  left: 0;
`;
const StyledBottomCenterTooltip = styled(StyledBottomTooltip) `
  ${horizontallyCenteredTooltip}
`;
const StyledBottomEndTooltip = styled(StyledBottomTooltip) `
  right: 0;
`;
// Left-positioned tooltips
const StyledLeftTooltip = styled(StyledTooltip) `
  right: 110%;

  &::after {
    border-right: 0;
    border-top-color: transparent;
    border-bottom-color: transparent;
    left: 100%;
    margin-left: -1px;
    ${verticallyCenteredTooltip}
  }
`;
const StyledLeftStartTooltip = styled(StyledLeftTooltip) `
  top: 0;
`;
const StyledLeftCenterTooltip = styled(StyledLeftTooltip) `
  ${verticallyCenteredTooltip}
`;
const StyledLeftEndTooltip = styled(StyledLeftTooltip) `
  bottom: 0;
`;
const StyledChildrenWrapper = styled.div ``;
// Mapper that relates a tooltip placement to a tooltip component.
const TOOLTIP_MAPPER = {
    [TooltipPlacement.TOP_START]: StyledTopStartTooltip,
    [TooltipPlacement.TOP_CENTER]: StyledTopCenterTooltip,
    [TooltipPlacement.TOP_END]: StyledTopEndTooltip,
    [TooltipPlacement.RIGHT_START]: StyledRightStartTooltip,
    [TooltipPlacement.RIGHT_CENTER]: StyledRightCenterTooltip,
    [TooltipPlacement.RIGHT_END]: StyledRightEndTooltip,
    [TooltipPlacement.BOTTOM_START]: StyledBottomStartTooltip,
    [TooltipPlacement.BOTTOM_CENTER]: StyledBottomCenterTooltip,
    [TooltipPlacement.BOTTOM_END]: StyledBottomEndTooltip,
    [TooltipPlacement.LEFT_START]: StyledLeftStartTooltip,
    [TooltipPlacement.LEFT_CENTER]: StyledLeftCenterTooltip,
    [TooltipPlacement.LEFT_END]: StyledLeftEndTooltip,
};
const getTooltipStyleRules = (tooltipRef, limitWidth) => {
    if (tooltipRef && tooltipRef.current && tooltipRef.current.clientWidth >= limitWidth) {
        return {
            width: limitWidth,
            whiteSpace: 'normal',
        };
    }
};
const Tooltip = ({ children, className = '', content, delayToOpen = 0, limitWidth = 300, onClick, onClose = () => { }, onOpen = () => { }, placement = TooltipPlacement.TOP_CENTER, preventFromOpening = false, showArrow = true, color, theme = DefaultTheme, }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [tooltipStyles, setTooltipStyles] = useState({});
    const tooltipRef = useRef(null);
    const childrenWrapperRef = useRef(null);
    const TooltipComponent = TOOLTIP_MAPPER[placement];
    useEffect(() => {
        setTooltipStyles(getTooltipStyleRules(tooltipRef, limitWidth));
    }, [preventFromOpening]);
    const handleMouseEnter = useCallback(debounce(() => {
        setIsOpen(true);
        onOpen();
    }, delayToOpen), [delayToOpen, onOpen]);
    const handleMouseLeave = useCallback(() => {
        handleMouseEnter.cancel();
        setIsOpen(false);
        onClose();
    }, [onClose]);
    return (React.createElement(ThemeProvider, { theme: theme },
        React.createElement(StyledTooltipContainer, { className: className, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave },
            React.createElement(StyledChildrenWrapper, { ref: childrenWrapperRef }, children),
            TooltipComponent && !preventFromOpening && (React.createElement(Portal, { contextualElementRef: childrenWrapperRef, style: { pointerEvents: onClick ? 'all' : 'none' } },
                React.createElement(TooltipComponent, { ref: tooltipRef, clickable: !!onClick, onClick: onClick, open: isOpen, showArrow: showArrow, style: tooltipStyles, color: color }, content))))));
};
export default Tooltip;
